// Contrato del bloque BACKOFFICE MDM de `loan-credit-business` — lo que alimenta la pantalla
// Control MDM (`/credit/mdm`). Fuente: `CONTRATO-BACKOFFICE-MDM.md`, decidido entre las 3 lanes.
//
// Viven en `openapi/public.yaml` («Public API (backoffice + RBAC)»), con W4Authorizer y permiso
// `mdm.*` scope TENANT. Son 10, y NO hay una de salud del proveedor: las métricas agregadas
// exigen recorrer el log de todo el tenant y esa tabla no tiene GSI.
//
//   GET  /backoffice/devices                            → la flota
//   GET  /backoffice/devices/{creditId}                 → el expediente de UN crédito
//   GET  /backoffice/devices/{creditId}/operations      → la bitácora del equipo
//   POST /backoffice/devices/{creditId}/lock            → bloquear
//   POST /backoffice/devices/{creditId}/unlock          → desbloquear
//   POST /backoffice/devices/{creditId}/pin-unlock      → el passcode para desbloquear sin señal
//   POST /backoffice/devices/{creditId}/extend-validity → desbloquear y reprogramar el corte
//   POST /backoffice/devices/{creditId}/retry-enroll    → relanzar el enrolamiento fallido
//   POST /backoffice/devices/{creditId}/refresh         → reconsultar al proveedor
//   POST /backoffice/devices/{creditId}/notify          → avisar en la pantalla del equipo
//   POST /backoffice/devices/{creditId}/release         → sacar el equipo del MDM, definitivo
//
// FECHAS: ISO-8601 en el wire (la tabla las guarda epoch ms; el mapper del lambda convierte).
//
// Al final viven dos contratos más: GET /borrowers/portfolio (bloque público, la cartera de un lote
// de clientes retail que alimenta la pantalla Clientes del Portal Vendedor) y GET /backoffice/credits
// (permiso `loan.credit.read` TENANT, el listado que alimenta la pantalla Cartera).
import type { ContractTypeEnum } from '@fiado/type-kit/bin/loanCredit/enums/ContractTypeEnum';
import type { LoanCreditResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/LoanCreditResponse';
import type { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { LoanDeviceMdmStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanDeviceMdmStatusEnum';
import type { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import type { MdmErrorCodeEnum } from '@fiado/type-kit/bin/mdm/enums/MdmErrorCodeEnum';
import type { MdmOperationStatusEnum } from '@fiado/type-kit/bin/mdm/enums/MdmOperationStatusEnum';
import type { DeviceNotifyIntensityEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceNotifyIntensityEnum';
import type { DeviceReleaseReasonEnum } from '@fiado/type-kit/bin/mdm/enums/DeviceReleaseReasonEnum';
import type { components, operations } from './loan-credit.gen';

type Schemas = components['schemas'];

/** Último fallo que el proveedor devolvió para ese equipo. */
export type LoanDeviceFleetError = Schemas['LoanDeviceFleetError'];

/** Una fila del expediente `LoanDeviceMdmMapping_GT`, tal como la entrega `GET /backoffice/devices`. */
export type LoanDeviceFleetItem = Schemas['LoanDeviceFleetItem'];

/** UNA página de la flota. El fin de la lista lo marca la AUSENCIA de `nextToken`. */
export type LoanDeviceFleetResponse = Schemas['LoanDeviceFleetResponse'];

/** `imei` (15 dígitos) busca exacto y no se combina con `status` ni `nextToken`. */
export type LoanDeviceFleetQuery = NonNullable<
    operations['backofficeListMdmDevices']['parameters']['query']
>;

/**
 * Una llamada al proveedor, tal como la guarda `LoanMdmOperationLog_GT`. `reason`, `comment` y
 * `actorEmail` faltan en las operaciones del sistema y en los asientos anteriores a esos campos.
 */
export type LoanDeviceOperationItem = Schemas['LoanDeviceOperationItem'];

/** Motivo asentado en la bitácora: la unión de los de bloqueo y los de desbloqueo. */
export type LoanDeviceOperationReason = Schemas['LoanDeviceOperationReasonEnum'];

/**
 * Una página de la bitácora de UN equipo.
 *
 * El fin de la lista lo marca la AUSENCIA de `nextToken`, nunca una página corta o vacía: DynamoDB
 * aplica el filtro después de leer la página.
 */
export type LoanDeviceOperationsPage = Schemas['LoanDeviceOperationLogResponse'];

/** Mensaje que el equipo bloqueado le muestra al cliente en pantalla. */
export interface LoanDeviceScreenMessage {
    title: string;
    content: string;
}

/** Body de `POST /backoffice/devices/{creditId}/lock`. `comment` admite hasta 200 caracteres. */
export type LoanDeviceLockRequest = Schemas['BackofficeDeviceLockRequest'];

/** Body de `POST /backoffice/devices/{creditId}/unlock`. Todo opcional; `comment` hasta 200. */
export type LoanDeviceUnlockRequest = Schemas['BackofficeDeviceUnlockRequest'];

export type LoanDeviceUnlockReason = Schemas['DeviceUnlockReasonEnum'];

/**
 * El fallo que el proveedor devolvió para UNA operación. No lleva instante: el `at` lo pone la
 * bitácora, no el resultado.
 */
export interface LoanDeviceOperationError {
    code: MdmErrorCodeEnum;
    message: string;
    providerDetails?: string;
}

/**
 * Hasta dónde llegó la extensión de vigencia. Son DOS llamadas al proveedor —desbloquear y
 * reprogramar el corte— y la fase dice cuál se completó:
 *
 * · `REJECTED` no salió del expediente · `UNLOCK_FAILED` nada cambió y el reintento es seguro ·
 * `SCHEDULE_FAILED` el equipo quedó LIBRE y SIN autolock · `COMPLETED` desbloqueó y reprogramó.
 *
 * Va como unión local y no como enum de `@fiado/type-kit`: el paquete no lo publica todavía.
 */
export type LoanDeviceExtendValidityPhase =
    | 'REJECTED'
    | 'UNLOCK_FAILED'
    | 'SCHEDULE_FAILED'
    | 'COMPLETED';

/** Body de `POST /backoffice/devices/{creditId}/extend-validity`. `comment` admite de 1 a 200. */
export type LoanDeviceExtendValidityRequest = Schemas['BackofficeDeviceExtendValidityRequest'];

/**
 * Resultado de extender la vigencia. Responde 200 aunque haya fallado: el rechazo viaja en
 * `status: ERROR` + `error`, igual que lock y unlock.
 */
export interface LoanDeviceExtendValidityResult {
    creditId: string;
    status: MdmOperationStatusEnum;
    error?: LoanDeviceOperationError;
    operationReference: string;
    mdmStatus: LoanDeviceMdmStatusEnum;
    phase: LoanDeviceExtendValidityPhase;
    unlockedAt?: string;
    /** Solo viaja cuando la reprogramación se instaló de verdad en el proveedor. */
    currentLockScheduledFor?: string;
}

/**
 * Resultado de relanzar un enrolamiento. Como el anterior, responde 200 con el desenlace adentro:
 * un estado que no admite reintento vuelve con `error.code`, no con un 4xx.
 */
export interface LoanDeviceEnrollResult {
    creditId: string;
    status: MdmOperationStatusEnum;
    error?: LoanDeviceOperationError;
    operationReference: string;
    /** Folio opaco del proveedor para seguir el enrolamiento asíncrono. */
    pollingHandle?: string;
    mdmStatus: LoanDeviceMdmStatusEnum;
}

/** Cómo terminó la consulta al proveedor. Lo que la ficha pinta en vez de un toast de color. */
export type MdmRefreshOutcome = Schemas['MdmRefreshOutcomeEnum'];

/** Por qué no se pudo afirmar nada. Sólo viaja con `NO_EVIDENCE`. */
export type MdmRefreshReason = Schemas['MdmRefreshReasonEnum'];

/** El veredicto de UNA consulta al proveedor: qué se observó, cuándo, y si el expediente cambió. */
export type LoanDeviceRefreshReview = Schemas['LoanDeviceRefreshReview'];

/**
 * Lo que devuelve `POST /backoffice/devices/{creditId}/refresh`: la fila YA actualizada y el
 * veredicto de la consulta. El `device` NO trae marca, modelo ni SKU: el silo no los resuelve aquí.
 */
export type LoanDeviceRefreshResult = Schemas['LoanDeviceRefreshResult'];

/** El aviso de cobranza que se muestra en el equipo; cada connector lo traduce a su proveedor. */
export type LoanDeviceNoticeType = Schemas['MdmNoticeTypeEnum'];

/**
 * Body de `POST /backoffice/devices/{creditId}/notify`. Viaja solo `noticeType`: el texto lo pone
 * el catálogo del connector, y `notificationCode`/`title`/`content` son legacy que el front no manda.
 */
export interface LoanDeviceNotifyRequest {
    intensity: DeviceNotifyIntensityEnum;
    noticeType: LoanDeviceNoticeType;
}

/**
 * Body de `POST /backoffice/devices/{creditId}/release`. La liberación es IRREVERSIBLE: el
 * expediente queda RELEASED y un segundo release vuelve con 200 y `error.code = CONFLICT`.
 */
export interface LoanDeviceReleaseRequest {
    reason: DeviceReleaseReasonEnum;
    /** Nota libre del operador, máximo 500 caracteres. Queda en el expediente del equipo. */
    comment?: string;
}

/**
 * Cómo se resolvió cada id del lote. `NO_BORROWER` es un dato POSITIVO —el cliente existe y no
 * tiene acreditado—; `UNAVAILABLE` es «no se pudo leer» e `INVALID_ID` es «ese id está mal formado».
 */
export type LoanPortfolioStatus = 'OK' | 'NO_BORROWER' | 'UNAVAILABLE' | 'INVALID_ID';

/**
 * De dónde salió el SCI. `BORROWER` es el vivo del acreditado y `CREDIT_ORIGINATION` el congelado
 * al originar el crédito más reciente. `NONE` es que no hay SCI que mostrar.
 */
export type LoanPortfolioSciSource = 'BORROWER' | 'CREDIT_ORIGINATION' | 'NONE';

/**
 * La cartera de UN cliente retail. Las cifras son opcionales a propósito: ausente = «no lo sé»,
 * presente = dato afirmado. `UNAVAILABLE` e `INVALID_ID` no traen ninguna.
 */
export interface LoanPortfolioItem {
    retailCustomerId: string;
    status: LoanPortfolioStatus;
    borrowerId?: string;
    /** Ausente para el vendedor, o con `status: OK` si el crédito no lo registró. Ausente NO es cero. */
    sciScore?: number;
    clientLevel?: ClientLevelEnum;
    sciSource?: LoanPortfolioSciSource;
    creditsCount?: number;
    activeCreditId?: string;
    /** Créditos VIVOS. Mayor a 1 es anomalía de datos: el saldo no es el de `activeCreditId`. */
    activeCreditsCount?: number;
    /** Suma de TODOS los créditos vivos, no solo el de `activeCreditId`. */
    outstandingBalanceCents?: number;
    /** Peor atraso entre los créditos vivos. `0` es al día; `null` es «sin cuotas fechadas». */
    arrearsDays?: number | null;
    settledCreditsCount?: number;
    writtenOffCreditsCount?: number;
    /** Para el vendedor: el cliente tiene un crédito vivo en otra cadena. Con alcance de tenant, `false`. */
    hasActiveCreditElsewhere?: Schemas['LoanPortfolioEntry']['hasActiveCreditElsewhere'];
}

/**
 * La cartera de un lote de clientes, en el orden pedido. `unavailableCount` hace ruidoso el
 * resultado parcial; los `INVALID_ID` no entran ahí porque reintentarlos nunca va a funcionar.
 */
export interface LoanPortfolioResponse {
    items: LoanPortfolioItem[];
    unavailableCount: number;
}

/**
 * Cómo le fue al silo leyendo el expediente MDM de ESE crédito. `NOT_ENROLLED` es un dato POSITIVO
 * —no hay equipo enrolado—, distinto de no haberlo podido leer o no poder verlo.
 */
export type LoanCreditDeviceStatus = 'RESOLVED' | 'NOT_ENROLLED' | 'UNREADABLE' | 'NO_PERMISSION';

/** El bloqueo del equipo financiado, resuelto por el silo dentro del mismo listado. */
export type LoanCreditPortfolioDevice = Schemas['CreditDeviceBlock'];

/**
 * Una fila de la cartera del tenant, tal como la entrega `GET /backoffice/credits`.
 *
 * El equipo financiado y la tienda viven en `saleContext`: el crédito no los guarda y el silo los
 * resuelve contra el wizard y el padrón de retail. El NOMBRE del cliente todavía no viaja.
 */
export interface LoanCreditPortfolioItem {
    creditId: string;
    borrowerId: string;
    status: LoanCreditStatusEnum;
    /** Nivel CONGELADO al originar. Ausente = el crédito no lo registró. */
    clientLevelAtOrigination?: ClientLevelEnum;
    weeklyPaymentWithIvaCents: number;
    termWeeks: number;
    equipmentPriceCents: number;
    downPaymentCents: number;
    financedCapitalCents: number;
    tnaAnnual: number;
    imei?: string;
    createdAt: string;
    activatedAt?: string;
    firstDueDate?: string;
    lastDueDate?: string;
    /** Cuotas `PAID` más `PAID_IN_ADVANCE`. */
    paidInstallments: number;
    /** `0` es «ya lo pagó»; `null` es «no aplica»: el crédito nunca llegó a contraer la deuda. */
    outstandingBalanceCents: number | null;
    /**
     * Deuda total: capital, interés e IVA de las cuotas abiertas. `null` en los mismos casos que el
     * saldo insoluto; ausente = el silo todavía no la publica.
     */
    totalDebtCents?: number | null;
    /** `0` es al día; `null` es «sin cuotas fechadas que mirar», que NO es lo mismo. */
    overdueDays: number | null;
    /**
     * Equipo y tienda de la venta que originó el crédito. Trae SU PROPIO estado por lectura: la
     * columna que no se pudo resolver llega declarada, nunca vacía y nunca como «no existe».
     */
    saleContext: LoanCreditSaleContext;
    /** El bloqueo del equipo, ya resuelto por el silo: la pantalla no cruza la flota. */
    device: LoanCreditPortfolioDevice;
}

/**
 * UNA página de la cartera del tenant. El fin de la lista lo marca la AUSENCIA de `nextToken`,
 * nunca una página corta: DynamoDB aplica el filtro después de leer la página.
 */
export interface LoanCreditPortfolioResponse {
    items: LoanCreditPortfolioItem[];
    /** Filas de ESTA página. Nunca el total de la cartera. */
    count: number;
    /** Cursor opaco a la siguiente página. Ausente = última. */
    nextToken?: string;
}

export interface LoanCreditPortfolioQuery {
    /** Un `LoanCreditStatusEnum`. Sin él, el lambda lista la cartera completa. */
    status?: LoanCreditStatusEnum;
    limit?: number;
    /** El `nextToken` de la página anterior. Ausente = primera página. */
    nextToken?: string;
}

/**
 * Enlace de descarga del PDF de un contrato — `GET /credits/{creditId}/contracts/{contractId}/url`.
 * `expiresAt` vence el ENLACE, no el documento.
 */
export interface LoanContractDownloadUrl {
    contractId: string;
    creditId: string;
    type: ContractTypeEnum;
    /** URL prefirmada de S3. Se abre tal cual y no se guarda: caduca. */
    url: string;
    expiresAt: string;
}

/* ================================================================================
   CRÉDITOS DE UN ACREDITADO — `GET /borrowers/{borrowerId}/credits`, lo que alimenta la pestaña
   «Historial crediticio» de la ficha del cliente.
   ================================================================================ */

/**
 * Cómo se resolvió una lectura al silo del wizard. `NOT_FOUND` es un dato POSITIVO —se buscó y no
 * hay venta— y `UNAVAILABLE` es «no se pudo leer»: colapsarlos en «sin dato» pierde la diferencia.
 */
export type LoanWizardLookupStatus = Schemas['WizardSessionLookupStatusEnum'];

/** Por qué la venta del wizard no se resolvió. Cada motivo pide algo distinto de quien lo lee. */
export type LoanSaleContextReason = Schemas['SaleContextReason'];

/** Cliente, equipo y tienda de la venta que originó el crédito; cada lectura trae su estado. */
export type LoanCreditSaleContext = Schemas['CreditSaleContext'];

/**
 * Un crédito del acreditado. Es el `LoanCreditResponse` de `@fiado/type-kit` más lo que el silo
 * agrega en este endpoint.
 *
 * 🔴 **Los agregados van OPCIONALES a propósito.** Ausente = «el silo todavía no lo publica» y NO
 * es lo mismo que `null` («lo sé y no aplica») ni que `0` (una afirmación). Pintar un cero donde el
 * campo no vino afirma una deuda que nadie leyó.
 */
export interface LoanBorrowerCreditItem extends LoanCreditResponse {
    /** Cupones cubiertos: `PAID` más `PAID_IN_ADVANCE`. */
    paidInstallments?: number;
    /** `0` es «ya no debe»; `null` es «no aplica»: el crédito nunca contrajo la deuda. */
    outstandingBalanceCents?: number | null;
    /** Capital, interés e IVA de las cuotas abiertas. `null` en los mismos casos que el saldo. */
    totalDebtCents?: number | null;
    /** `0` es al día; `null` es «no hay atraso que medir». */
    overdueDays?: number | null;
    /** Total con IVA de las cuotas vencidas impagas. `null` cuando `overdueDays` es `null`. */
    overdueAmountCents?: number | null;
    saleContext?: LoanCreditSaleContext;
}

/* ================================================================================
   DETALLE DE UN CRÉDITO — `GET /credits/{creditId}`, lo que alimenta la ficha 360.
   ================================================================================ */

/**
 * El `LoanCreditResponse` de type-kit más lo que el OpenAPI agrega en el detalle: equipo, avance y venta.
 * La base queda en type-kit porque sus enums son los que consume el resto de la ficha.
 * `totalDebtCents` va opcional: ausente = el silo todavía no la publica.
 */
export type LoanCreditDetail = LoanCreditResponse &
    Omit<Schemas['LoanCreditDetail'], keyof LoanCreditResponse | 'totalDebtCents'> & {
        totalDebtCents?: number | null;
    };

/* ================================================================================
   CLIENTES DE CRÉDITO — `GET /backoffice/credit-customers`, la ÚNICA llamada que arma la tabla
   de `/credit/customers`. El BFF cruza padrón, cartera y segmentos; el front ya no cruza nada.
   ================================================================================ */

export type { CreditLastPayment } from '@fiado/type-kit/bin/loanCredit/dtos/responses/CreditCustomerListItem';
import type { CreditLastPayment } from '@fiado/type-kit/bin/loanCredit/dtos/responses/CreditCustomerListItem';

/** Un segmento del cliente, tal como lo entrega el BFF. Sin color: el listado no lo publica. */
export interface CreditCustomerSegmentRef {
    segmentId: string;
    name: string;
}

/**
 * En qué quedó la lectura de crédito de ESE cliente. `NO_BORROWER` es un dato POSITIVO —el cliente
 * existe y no tiene acreditado— y `UNAVAILABLE` es «no se pudo leer». Ninguno de los dos es un 0.
 */
export type CreditCustomerCreditState = 'READ' | 'NO_BORROWER' | 'UNAVAILABLE';

/**
 * La cartera del cliente resuelta por el BFF. Con `state` distinto de `READ` las cifras no valen:
 * el front pinta el motivo, nunca un cero que finge dato.
 */
export interface CreditCustomerCredit {
    state: CreditCustomerCreditState;
    clientLevel: ClientLevelEnum | null;
    sciScore: number | null;
    sciSource: LoanPortfolioSciSource | null;
    creditsCount: number;
    activeCreditsCount: number;
    settledCreditsCount: number;
    outstandingBalanceCents: number | null;
    /** Deuda total de sus créditos vivos. Ausente = el silo todavía no la publica. */
    totalDebtCents?: number | null;
    /** `0` es al día; `null` es «no hay atraso que medir». */
    arrearsDays: number | null;
    /** Instante ISO del último pago aplicado. `null` = el cliente todavía no pagó ninguna cuota. */
    lastPaymentAt: string | null;
    /** Fecha y lugar del último pago; ausente en créditos sin pago desde que existe el campo. */
    lastPayment?: CreditLastPayment;
    activeCreditId: string | null;
}

/** Una fila de la tabla Clientes, ya lista para pintar: el front no cruza nada más. */
export interface CreditCustomerListItem {
    retailCustomerId: string;
    /** Nombre compuesto por el silo de retail. `null` = la venta nunca lo resolvió. */
    fullName: string | null;
    phone: string | null;
    segments: CreditCustomerSegmentRef[];
    /** `false` = la lectura de segmentos falló: la celda dice «sin leer», no «ninguno». */
    segmentsRead: boolean;
    credit: CreditCustomerCredit;
}

/**
 * Una página de clientes. `count` son las filas de ESTA página, nunca el total; la ausencia de
 * `nextToken` es lo único que marca el fin de la lista.
 */
export interface CreditCustomersPage {
    items: CreditCustomerListItem[];
    count: number;
    nextToken?: string;
}

export interface CreditCustomersQuery {
    /** 1-100, default 25 del lado del lambda. */
    limit?: number;
    /** Cursor opaco de la página anterior. Se pasea tal cual, no se compone ni se re-codifica. */
    nextToken?: string;
}

/* ================================================================================
   CUOTAS DE TODA LA CARTERA — `GET /backoffice/installments`, lo que arma la tabla de
   `/credit/installments`. Permiso `loan.credit.read`, scope TENANT.
   El orden lo entrega el índice: ASCENDENTE por `dueDate`, la más vencida primero.
   ================================================================================ */

/**
 * Una cuota de la cartera del tenant con su crédito, su equipo y su cliente. Los días de mora de la
 * cuota se DERIVAN de `dueDate` contra hoy.
 */
export type LoanInstallmentListItem = Schemas['LoanInstallmentListItem'];

/** Las cifras del crédito de la cuota, con la misma receta que Cartera. */
export type LoanInstallmentCreditContext = Schemas['LoanInstallmentCreditContext'];

/** El nombre del cliente de la cuota. `UNAVAILABLE` es «no se pudo leer», nunca «sin nombre». */
export type LoanInstallmentSaleContext = Schemas['LoanInstallmentSaleContext'];

/** El bloqueo del equipo resuelto por el silo; `mdmStatus` sólo viaja con `status: RESOLVED`. */
export type LoanCreditDeviceBlock = Schemas['CreditDeviceBlock'];

/**
 * Listado: `status` obligatorio y `nextToken` atado a ese `status`. Búsqueda: `creditId` O `imei`,
 * nunca los dos, en una sola página sin `nextToken`.
 */
export type LoanInstallmentListQuery = NonNullable<
    operations['backofficeListInstallments']['parameters']['query']
>;

/** UNA página de cuotas. El fin de la lista lo marca la AUSENCIA de `nextToken`. */
export type LoanInstallmentListResponse = Schemas['LoanInstallmentListResponse'];

/* ================================================================================
   PAGOS DE UN CRÉDITO — `GET /credits/{creditId}/payments`, del más reciente al más viejo.
   ================================================================================ */

/** Un pago que el motor de cobro asentó en `LoanPayment_GT`. */
export type LoanPaymentListItem = Schemas['LoanPaymentListItem'];

/** UNA página de pagos. El fin de la lista lo marca la AUSENCIA de `nextToken`. */
export type LoanPaymentListResponse = Schemas['LoanPaymentListResponse'];

export type LoanPaymentListQuery = NonNullable<
    operations['publicListCreditPayments']['parameters']['query']
>;

export type CollectionMechanism = Schemas['CollectionMechanismEnum'];

export type CollectionResultStatus = Schemas['CollectionResultStatusEnum'];

/* ================================================================================
   MENSAJES DE UN CRÉDITO — `GET /backoffice/credits/{creditId}/notifications`, del más nuevo al
   más viejo, en páginas de 20. Descartes incluidos.
   ================================================================================ */

/** Un aviso de la bitácora del crédito, con el texto que recibió el cliente si se pudo leer. */
export type LoanCreditNotificationItem = Schemas['CreditNotificationItem'];

/** UNA página de avisos. El fin de la lista lo marca la AUSENCIA de `nextToken`. */
export type LoanCreditNotificationListResponse = Schemas['CreditNotificationListResponse'];

export type LoanCreditNotificationListQuery = NonNullable<
    operations['backofficeListCreditNotifications']['parameters']['query']
>;

export type LoanNotificationChannel = NonNullable<LoanCreditNotificationItem['channel']>;

export type LoanNotificationDiscardReason = NonNullable<
    LoanCreditNotificationItem['discardReason']
>;

export type LoanNotificationDeliveryStatus = Schemas['NotificationDeliveryStatusEnum'];

export type LoanNotificationBodyStatus = Schemas['NotificationBodyStatusEnum'];

/** El último aviso no descartado del crédito, tal como viaja en cada cuota del listado. */
export type LoanInstallmentLastNotification = Schemas['LoanInstallmentLastNotification'];

// Cartera del vendedor (`GET /seller/credits` y `/seller/credits/{creditId}`, pantalla Cobro).
// Los tres contratos son los de type-kit; sin DTO local.
export type { SellerCreditDetailResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerCreditDetailResponse';
export type { SellerCreditListResponse } from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerCreditListResponse';
export type { SellerCreditRow } from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerCreditRow';

// Cobro en tienda: el contrato es el de type-kit; sin DTO local.
export type { CreateSellerPaymentRequest } from '@fiado/type-kit/bin/loanCredit/dtos/requests/CreateSellerPaymentRequest';
export type { InStorePaymentDetail } from '@fiado/type-kit/bin/loanCredit/dtos/requests/InStorePaymentDetail';
export type {
    NamedRef,
    SellerNextInstallment,
    SellerPaymentCredit,
    SellerPaymentResult,
} from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerPaymentResult';
export type {
    SellerCreditPaymentSummary,
    SellerPaymentOption,
    SellerPaymentOptions,
} from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerPaymentOptions';
export type { SellerCreditLookupResult } from '@fiado/type-kit/bin/loanCredit/dtos/responses/SellerCreditLookupResult';
export type {
    InStorePaymentListItem,
    InStorePaymentListResponse,
} from '@fiado/type-kit/bin/loanCredit/dtos/responses/InStorePaymentListItem';
export { InStorePaymentMethodEnum } from '@fiado/type-kit/bin/loanCredit/enums/InStorePaymentMethodEnum';
export { LoanPaymentOriginEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanPaymentOriginEnum';

/** Rango y página de la conciliación de una tienda (`from`/`to` ISO-8601, máximo 31 días). */
export type InStorePaymentListQuery =
    operations['publicListStoreInStorePayments']['parameters']['query'];

/* ================================================================================
   ABONO A CAPITAL — `GET /backoffice/credits/{creditId}/prepayment-quote` y
   `POST /backoffice/credits/{creditId}/prepayments`, permiso `loan.credit.prepay`.
   ================================================================================ */

/** Saldo insoluto, pago del periodo, mínimo del abono (según el plan) y monto de liquidación. */
export type LoanPrepaymentQuote = Schemas['LoanPrepaymentQuote'];

/** El monto del abono en cents; sin motivo, OTP ni llave de idempotencia. */
export type BackofficeApplyPrepaymentRequest = Schemas['BackofficeApplyPrepaymentRequest'];

/** El cargo que se le pidió al motor (`creditId#PREPAYMENT#n`). El libro se mueve con su resultado. */
export type LoanAdHocChargeRequested = Schemas['LoanAdHocChargeRequested'];

/** Mensajes de cobranza del cliente por día de tienda y canal: enviados y bloqueados por el tope. */
export type CollectionQuotaResponse = Schemas['CollectionQuotaResponse'];

/** Un día de tienda en un canal del contador de cobranza. */
export type CollectionQuotaDay = Schemas['CollectionQuotaDay'];

/** Body del envío manual de un momento del crédito. */
export type CustomerManualMessageRequest = Schemas['CustomerManualMessageRequest'];

/** El id del mensaje que quedó encolado. */
export type CustomerManualMessageResponse = Schemas['CustomerManualMessageResponse'];
